import { Component, inject, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FeedbackButtonComponent } from './components/shared/feedback-button/feedback-button.component';
import { FloatingDialogsHostComponent } from './components/shared/floating-dialogs-host/floating-dialogs-host.component';
import {
  HeaderComponent,
  HeaderMode,
} from './components/shared/header/header.component';
import { isAuthBridgeWindow } from './config/auth-bridge.config';
import { AuthService } from './services/auth.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    FloatingDialogsHostComponent,
    FeedbackButtonComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  private router = inject(Router);
  private authService = inject(AuthService);

  protected readonly title = signal('stb-staffing-ui');
  headerMode: HeaderMode = HeaderMode.LOGIN;

  // Controls the floating feedback button: shown to authenticated users on all pages.
  protected readonly showFeedbackButton = signal(false);

  /**
   * True when this window was opened on the MSAL redirect-bridge page (auth
   * popups land there; the redirect fallback passes through it). The bridge
   * page must stay free of MSAL activity — no bootstrap, no header (whose
   * child services acquire tokens) — so the shell renders only the router
   * outlet and skips all auth work.
   */
  protected readonly isAuthBridgeRoute = isAuthBridgeWindow();

  ngOnInit(): void {
    if (this.isAuthBridgeRoute) {
      return;
    }
    // Trigger shared bootstrap — initializes MSAL, consumes the redirect promise once,
    // and loads user state. shareReplay in bootstrap$ ensures this fires only one
    // HTTP call even though App, LoginComponent, and DashboardComponent all subscribe.
    this.authService.bootstrap$.subscribe({
      error: (error) =>
        console.error('Error during auth bootstrap at app level:', error),
    });

    // Listen to router events to determine header mode
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(async (event: NavigationEnd) => {
        // Check if user is authenticated
        const isAuthenticated = await this.authService.isLoggedInAsync();

        // Determine header mode based on URL and authentication status
        this.headerMode = this.getHeaderMode(event.url, isAuthenticated);

        // Show the feedback button on authenticated pages
        this.showFeedbackButton.set(
          this.shouldShowFeedbackButton(event.url, isAuthenticated),
        );
      });
  }

  private shouldShowFeedbackButton(url: string, isAuthenticated: boolean): boolean {
    if (!isAuthenticated) {
      return false;
    }

    // Hide only on the login page; shown everywhere else
    return !url.includes('/login');
  }

  private getHeaderMode(url: string, isAuthenticated: boolean): HeaderMode {
    // Login page or root when not authenticated
    if (url.includes('/login') || url === '/' || !isAuthenticated) {
      return HeaderMode.LOGIN;
    }

    // All other pages when authenticated
    return HeaderMode.AUTHENTICATED;
  }
}
